import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { api, API } from "@/lib/api";
import { useUser } from "@/lib/userContext";
import SiteHeader from "@/components/SiteHeader";
import { useProjectAutosave } from "@/hooks/useProjectAutosave";
import { Field, Chip, SectionTitle } from "@/components/questionnaire/FormPrimitives";
import { money } from "@/lib/brand";
import { Progress } from "@/components/ui/progress";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ChevronLeft, ChevronRight, Plus, Trash2, ImagePlus, Save, Sparkles, X, ArrowUp, ArrowDown, Info, ShoppingBag } from "lucide-react";

const TOTAL_STEPS = 12;

const TONE_OPTIONS = [
  "Heartfelt","Funny","Nostalgic","Emotional","Warm and grateful","Light and playful","Inspirational","Poetic","Simple and honest","Premium and elegant","Mix of funny + emotional"
];

const DEFAULT_CHAPTERS = [
  "Opening dedication","How it all began","The moments that stayed","The little things about you","The memory I'll never forget","What you mean to me","Final letter / closing message"
];

function makeEmptyCharacter() {
  return { name: "", nickname: "", relationship: "", age_range: "", personality: "", physical_description: "", quirks: "", catchphrases: "", emotional_role: "", photo_ids: [], auto: false };
}
function makeEmptyMemory() {
  return { title: "", date: "", location: "", people_involved: "", story_details: "", why_matters: "", never_forget: "", dialogue: "", avoid_notes: "", emotion: "", importance_rating: 3 };
}

export default function QuestionnairePage() {
  const { projectId: routeId } = useParams();
  const navigate = useNavigate();
  const { userId } = useUser();
  const [searchParams] = useSearchParams();
  const creation = useRef(null);

  const [projectId, setProjectId] = useState(routeId === "new" ? null : routeId);
  const [meta, setMeta] = useState(null);
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");
  const [validation, setValidation] = useState("");
  const [navigating, setNavigating] = useState(false);
  const [uploading, setUploading] = useState(false);

  // Form data — flat for convenience
  const [d, setD] = useState({
    relationship_type: "",
    recipient_name: "",
    recipient_nickname: "",
    your_name: "",
    your_book_name: "",
    relationship_oneliner: "",
    occasion: "",
    occasion_date: "",
    selected_product: "classic",
    tones: [],
    characters: [],
    photos: [],
    // AI photo generation preferences
    ai_photos_enabled: false,
    ai_photo_style: "",
    ai_photo_notes: "",
    ai_photo_reference_ids: [],
    main_memory: makeEmptyMemory(),
    memories: [makeEmptyMemory()],
    opening_dedication: "",
    final_message: "",
    never_said: "",
    feel_after_reading: "",
    final_page_style: "",
    narration: "first_person",
    language: "simple",
    writing_styles: ["Simple and heartfelt"],
    book_length: "essential",
    chapters: [...DEFAULT_CHAPTERS],
    avoid_topics: "",
    avoid_people: "",
    sensitive_memories: "",
    avoid_emotional: false,
    avoid_romantic: false,
    avoid_jokes: "",
  });

  // Load before enabling writes. Old projects retain their original step order and fields.
  useEffect(() => {
    let active = true;
    const load = async () => {
      setLoading(true);
      setLoadError("");
      try {
        const { data: m } = await api.get("/meta/options");
        if (!active) return;
        setMeta(m);
        if (routeId === "new") {
          const relationship = searchParams.get("relationship");
          const occasion = searchParams.get("occasion");
          if (!creation.current) creation.current = api.post("/projects", { user_id: userId, title: "My Keepsake", data: { relationship_type: m.relationships.includes(relationship) ? relationship : "", occasion: m.occasions.includes(occasion) ? occasion : "" } });
          const { data: created } = await creation.current;
          if (active) navigate(`/create/${created.id}`, { replace: true });
          return;
        }
        if (routeId) {
          creation.current = null;
          const { data: p } = await api.get(`/projects/${routeId}`);
          if (!active) return;
          setProjectId(routeId);
          try {
            const recovery = JSON.parse(sessionStorage.getItem(`ks_recovery_${routeId}`) || "null");
            if (recovery?.data && recovery.updatedAt > Date.parse(p.updated_at)) { p.data = recovery.data; p.current_step = recovery.current_step; }
          } catch (_) { /* Keep server data if local recovery is unavailable. */ }
          if (p?.data) {
            // Migrate legacy single-tone fields to the new `tones` list
            const migratedTones = (p.data.tones && p.data.tones.length)
              ? p.data.tones
              : [p.data.tone_primary, p.data.tone_secondary].filter(Boolean);
            setD((prev) => ({ ...prev, ...p.data,
              tones: migratedTones,
              characters: (p.data.characters && p.data.characters.length) ? p.data.characters : [],
              memories: (p.data.memories && p.data.memories.length) ? p.data.memories : [makeEmptyMemory()],
              main_memory: p.data.main_memory || makeEmptyMemory(),
              chapters: (p.data.chapters && p.data.chapters.length) ? p.data.chapters : [...DEFAULT_CHAPTERS],
              photos: p.data.photos || [],
              writing_styles: (p.data.writing_styles && p.data.writing_styles.length) ? p.data.writing_styles : (p.data.writing_style ? [p.data.writing_style] : ["Simple and heartfelt"]),
            }));
            // Map legacy book_length values to the new tier keys
            const legacyMap = { short: "essential", standard: "extended", detailed: "detailed" };
            if (p.data.book_length && legacyMap[p.data.book_length]) {
              setD((prev) => ({ ...prev, book_length: legacyMap[p.data.book_length] }));
            }
            if (p.current_step) setStep(Math.max(1, Math.min(p.current_step, TOTAL_STEPS)));
          }
          try { localStorage.setItem("ks_last_project", routeId); } catch (_) { /* Server persistence remains available. */ }
          setLoading(false);
        }
      } catch (e) {
        creation.current = null;
        if (active) { setLoadError("We couldn’t open this book. Please try again or return to your saved books."); setLoading(false); }
      }
    };
    if (userId) load();
    return () => { active = false; };
    // Search parameters only preselect a newly created book.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId, routeId, navigate]);

  const { saveState, flush } = useProjectAutosave(projectId, d, step, !loading && !loadError && routeId !== "new");

  const set = (key, value) => setD((prev) => ({ ...prev, [key]: value }));

  const progressPct = useMemo(() => Math.round(((step - 1) / (TOTAL_STEPS - 1)) * 100), [step]);

  const navigateStep = async (target) => {
    if (target > step && step === 1 && (!d.recipient_name.trim() || !d.relationship_type)) {
      setValidation("Choose your relationship and enter your recipient’s name to continue."); return;
    }
    if (target > step && step === 6 && !d.main_memory?.story_details?.trim()) {
      setValidation("Share a few words about your main memory. Rough notes are perfect."); return;
    }
    setNavigating(true);
    if (await flush()) { setValidation(""); setStep(Math.max(1, Math.min(TOTAL_STEPS, target))); window.scrollTo({ top: 0, behavior: "instant" }); }
    else setValidation("Your changes haven’t saved yet. Check your connection and try again.");
    setNavigating(false);
  };
  const next = () => navigateStep(step + 1);
  const back = () => navigateStep(step - 1);
  useEffect(() => { if (!loading) document.getElementById("questionnaire-step-title")?.focus({ preventScroll: true }); }, [step, loading]);

  const onUploadPhotos = async (files) => {
    if (!files || files.length === 0) return;
    const remaining = Math.max(0, 10 - d.photos.length);
    const list = Array.from(files).slice(0, remaining);
    if (!remaining) { toast.info("You can include up to 10 photos."); return; }
    setUploading(true);
    for (const f of list) {
      const fd = new FormData();
      fd.append("file", f);
      fd.append("user_id", userId);
      fd.append("project_id", projectId);
      try {
        const res = await fetch(`${API}/photos/upload`, { method: "POST", body: fd });
        if (!res.ok) throw new Error("upload failed");
        const data = await res.json();
        setD((prev) => ({
          ...prev,
          photos: [...prev.photos, {
            photo_id: data.id,
            url: `${API}/photos/${data.id}`,
            filename: data.filename,
            caption: "", people_in_photo: "", when_taken: "", where_taken: "", why_important: "",
            use_in_book: true, use_as_character_reference: false,
          }],
        }));
      } catch (e) {
        toast.error(`Could not upload ${f.name}`);
      }
    }
    setUploading(false);
  };

  const onUploadAIRefs = async (files) => {
    if (!files || files.length === 0) return;
    const list = Array.from(files).slice(0, Math.max(0, 5 - d.ai_photo_reference_ids.length));
    setUploading(true);
    for (const f of list) {
      const fd = new FormData();
      fd.append("file", f);
      fd.append("user_id", userId);
      fd.append("project_id", projectId);
      try {
        const res = await fetch(`${API}/photos/upload`, { method: "POST", body: fd });
        if (!res.ok) throw new Error("upload failed");
        const data = await res.json();
        setD((prev) => ({ ...prev, ai_photo_reference_ids: [...(prev.ai_photo_reference_ids || []), data.id] }));
      } catch (e) {
        toast.error(`Could not upload ${f.name}`);
      }
    }
    setUploading(false);
  };

  const placeOrder = async () => {
    if (!projectId) return;
    if (!d.recipient_name.trim() || !d.relationship_type || !d.main_memory?.story_details?.trim()) {
      setValidation("Add a recipient and relationship in step 1, and a main memory in step 6, before continuing."); return;
    }
    setNavigating(true);
    try {
      if (!(await flush())) throw new Error("Save failed");
      // Save final state then navigate to product selection (then checkout)
      await api.put(`/projects/${projectId}`, { data: d, current_step: TOTAL_STEPS, status: "ready_to_order" });
      navigate(`/product/${projectId}`);
    } catch (e) {
      toast.error("Could not save your project. Please try again.");
    } finally { setNavigating(false); }
  };

  if (loadError) return <><SiteHeader/><main id="main-content" className="form-shell"><p role="alert" data-testid="project-load-error">{loadError}</p><Link to="/projects" className="btn-ghost mt-6" data-testid="project-load-back">Your saved books</Link><button className="btn-primary mt-6 ml-3" onClick={() => window.location.reload()} data-testid="project-retry">Try again</button></main></>;

  if (loading || !meta) {
    return (
      <div className="min-h-screen bg-cream">
        <SiteHeader />
        <div className="max-w-3xl mx-auto px-4 py-20 text-center">
          <p className="font-serif text-2xl text-ink">Preparing your storybook…</p>
          <p className="text-sm text-inkMute mt-2">Just a moment.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-cream">
      <SiteHeader showNav={false} />
      <main id="main-content" className="form-shell">
        {/* Progress */}
        <div className="mb-8">
          <div className="form-price"><span data-testid="creation-starting-price">{meta.products?.classic?.price ? `Storybooks from ${money(meta.products.classic.price)}` : "Keepsake Storybook"}. Add-ons are extra.</span><Link to="/projects" data-testid="form-saved-books" className="underline underline-offset-4 lg:hidden">Your books</Link></div>
          <div className="form-phases" aria-label="Creation phases" data-testid="creation-phases">{[["The Person",1,2],["The Memories",3,7],["The Story",8,11],["Your Keepsake",12,12]].map(([label,first,last],i) => <div key={label} className="form-phase" aria-current={step >= first && step <= last ? "step" : undefined} data-testid={`creation-phase-${i+1}`}><span>0{i+1}</span>{label}</div>)}</div>
          <div className="flex items-center justify-between gap-3 text-xs text-inkMute mb-3">
            <span data-testid="questionnaire-step-count">Step {step} of {TOTAL_STEPS}</span>
            <span className="save-status" data-state={saveState} role="status" aria-live="polite" data-testid="save-status"><Save size={12}/>{({ loading:"Loading",pending:"Unsaved changes",saving:"Saving…",saved:"Saved",error:"Not saved" })[saveState]}{saveState === "error" && <button className="underline" onClick={flush} data-testid="retry-save">Retry</button>}</span>
          </div>
          <Progress value={progressPct} aria-label={`Step ${step} of ${TOTAL_STEPS}`} data-testid="questionnaire-progress" className="h-1 bg-lineSoft [&>div]:bg-chocolate" />
        </div>

        <div className="form-panel" aria-busy={navigating}>
          {step === 1 && <Step1 d={d} set={set} meta={meta} />}
          {step === 2 && <Step2 d={d} set={set} meta={meta} />}
          {step === 3 && <Step4 d={d} setD={setD} meta={meta} />}
          {step === 4 && <Step5 d={d} setD={setD} />}
          {step === 5 && <Step6 d={d} setD={setD} onUploadPhotos={onUploadPhotos} onUploadAIRefs={onUploadAIRefs} meta={meta} uploading={uploading} />}
          {step === 6 && <Step7 d={d} setD={setD} />}
          {step === 7 && <Step8 d={d} setD={setD} />}
          {step === 8 && <Step9 d={d} set={set} />}
          {step === 9 && <Step10 d={d} set={set} setD={setD} meta={meta} />}
          {step === 10 && <Step11 d={d} setD={setD} meta={meta} />}
          {step === 11 && <Step12 d={d} set={set} />}
          {step === 12 && <Step13 d={d} />}
        </div>
        {validation && <p className="field-error" role="alert" data-testid="questionnaire-validation">{validation}</p>}
        <div className="form-navigation">
          <button onClick={back} disabled={step === 1 || navigating || uploading} className="btn-ghost disabled:opacity-40" data-testid="back-btn">
            <ChevronLeft size={16} className="mr-1.5" /> Back
          </button>
          {step < TOTAL_STEPS ? (
            <button onClick={next} disabled={navigating || uploading} className="btn-primary" data-testid="next-btn">
              Continue <ChevronRight size={16} className="ml-1.5" />
            </button>
          ) : (
            <button onClick={placeOrder} disabled={navigating} className="btn-primary" data-testid="place-order-btn">
              <ShoppingBag size={16} className="mr-1.5" /> Choose your book
            </button>
          )}
        </div>
        <p className="text-xs text-inkMute mt-6" data-testid="save-explanation">Return on this browser to find your saved book. Only “Saved” confirms your latest answers reached us.</p>
      </main>
    </div>
  );
}

/* ---------- Step components ---------- */

function Step1({ d, set, meta }) {
  return (
    <div>
      <SectionTitle overline="Step 1 · Your person" title="Who makes your story special?" hint="Start with their name and your relationship. The little details can come later." />
      <Field label="Recipient’s name" required><Input value={d.recipient_name} onChange={(e) => set("recipient_name", e.target.value)} className="k-input" placeholder="e.g., Anita" data-testid="recipient-name" autoComplete="off" /></Field>
      <p className="k-label" id="relationship-label">Your relationship to them <span className="text-chocolate">*</span></p>
      <div className="flex flex-wrap gap-2 mb-6" role="group" aria-labelledby="relationship-label">
        {meta.relationships.map((r) => (
          <Chip key={r} active={d.relationship_type === r} onClick={() => set("relationship_type", r)} testId={`rel-${r}`}>{r}</Chip>
        ))}
      </div>
      <details className="border-t border-lineSoft pt-5" data-testid="recipient-optional-details"><summary className="text-sm cursor-pointer min-h-[44px]" data-testid="recipient-optional-toggle">A few personal touches <span className="text-inkMute">(optional)</span></summary>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-4">
        <Field label="Nickname or what you call them"><Input value={d.recipient_nickname} onChange={(e) => set("recipient_nickname", e.target.value)} className="k-input" placeholder="e.g., Mumma" data-testid="recipient-nickname" /></Field>
        <Field label="Your name"><Input value={d.your_name} onChange={(e) => set("your_name", e.target.value)} className="k-input" placeholder="e.g., Aarti" data-testid="your-name" /></Field>
        <Field label="What should the book call you?"><Input value={d.your_book_name} onChange={(e) => set("your_book_name", e.target.value)} className="k-input" placeholder="e.g., Your daughter" data-testid="your-book-name" /></Field>
      </div>
      <Field label="Describe the relationship in one line"
        hint='Example: "She is my mom, my biggest supporter, and the person who always believed in me."'>
        <Textarea value={d.relationship_oneliner} onChange={(e) => set("relationship_oneliner", e.target.value)} className="k-input min-h-[80px]" data-testid="relationship-oneliner" />
      </Field>
      </details>
      <p className="text-xs text-inkMute mt-5">No writing experience needed. A few honest memories are a lovely place to start.</p>
    </div>
  );
}

function Step2({ d, set, meta }) {
  return (
    <div>
      <SectionTitle overline="Step 2 · Occasion" title="What is the occasion?" />
      <div className="flex flex-wrap gap-2 mb-6">
        {meta.occasions.map((o) => (
          <Chip key={o} active={d.occasion === o} onClick={() => set("occasion", o)} testId={`occasion-${o}`}>{o}</Chip>
        ))}
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <Field label="Occasion date" hint={d.occasion_date ? `Selected: ${d.occasion_date.split("-").reverse().join("/")} (DD/MM/YYYY)` : "DD/MM/YYYY. Your date picker may use your device’s format."}><Input type="date" lang="en-IN" value={d.occasion_date} onChange={(e) => set("occasion_date", e.target.value)} className="k-input" data-testid="occasion-date" /></Field>
      </div>
    </div>
  );
}

function Step4({ d, setD, meta }) {
  const toggleTone = (t) => {
    setD((prev) => {
      const list = new Set(prev.tones || []);
      if (list.has(t)) list.delete(t);
      else list.add(t);
      return { ...prev, tones: Array.from(list) };
    });
  };
  return (
    <div>
      <SectionTitle overline="Step 3 · Tone" title="How should the book feel?" hint="Pick one or several tones. We’ll blend them across your story." />
      <Label className="k-label">Tones</Label>
      <div className="flex flex-wrap gap-2">
        {meta.tones.map((t) => (
          <Chip key={t} active={(d.tones || []).includes(t)} onClick={() => toggleTone(t)} testId={`tone-${t}`}>{t}</Chip>
        ))}
      </div>
    </div>
  );
}

function Step5({ d, setD }) {
  const updateChar = (i, key, value) => {
    setD((prev) => {
      const list = [...prev.characters];
      list[i] = { ...list[i], [key]: value };
      return { ...prev, characters: list };
    });
  };
  const addChar = () => setD((prev) => ({ ...prev, characters: [...prev.characters, makeEmptyCharacter()] }));
  const removeChar = (i) => setD((prev) => ({ ...prev, characters: prev.characters.filter((_, idx) => idx !== i) }));

  // Auto-include recipient + user from Step 1 the first time we land here, if not already present
  useEffect(() => {
    setD((prev) => {
      const list = [...(prev.characters || [])];
      const hasRecipient = list.some((c) => c.relationship === "Recipient (you're writing for them)");
      const hasUser = list.some((c) => c.relationship === "You (the gift-giver)");
      let changed = false;
      if (prev.recipient_name && !hasRecipient) {
        list.unshift({
          ...makeEmptyCharacter(),
          name: prev.recipient_name,
          nickname: prev.recipient_nickname || "",
          relationship: "Recipient (you're writing for them)",
          auto: true,
        });
        changed = true;
      }
      if (prev.your_name && !hasUser) {
        list.push({
          ...makeEmptyCharacter(),
          name: prev.your_name,
          nickname: prev.your_book_name || "",
          relationship: "You (the gift-giver)",
          auto: true,
        });
        changed = true;
      }
      return changed ? { ...prev, characters: list } : prev;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div>
      <SectionTitle overline="Step 4 · Characters" title="Who else appears in the story?" hint="You and the recipient are already added below. Add any other people, family, friends, or pets who matter to the story." />
      <div className="flex items-start gap-2 rounded-xl bg-cream border border-lineSoft p-4 mb-5 text-sm text-ink">
        <Info size={16} className="text-chocolate shrink-0 mt-0.5" />
        <p>The people you named in step 1 are added below. Edit or remove their details, and add anyone else who belongs in the story.</p>
      </div>
      <div className="space-y-5">
        {d.characters.map((c, i) => (
          <div key={i} className={`rounded-xl border p-5 ${c.auto ? 'bg-surface border-lineSoft' : 'bg-cream border-lineSoft'}`}>
            <div className="flex items-center justify-between mb-4">
              <p className="font-serif text-lg text-ink">
                {c.auto ? (c.relationship === "Recipient (you're writing for them)" ? "Recipient · auto-added" : "You · auto-added") : `Character ${i + 1}`}
              </p>
              <button onClick={() => removeChar(i)} className="text-xs text-inkMute hover:text-chocolate inline-flex items-center gap-1" data-testid={`remove-char-${i}`}>
                <Trash2 size={14} /> Remove
              </button>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Field label="Name"><Input value={c.name} onChange={(e) => updateChar(i, "name", e.target.value)} className="k-input" data-testid={`char-name-${i}`} /></Field>
              <Field label="Nickname"><Input value={c.nickname} onChange={(e) => updateChar(i, "nickname", e.target.value)} className="k-input" data-testid={`char-nickname-${i}`} /></Field>
              <Field label="Relationship to recipient"><Input value={c.relationship} onChange={(e) => updateChar(i, "relationship", e.target.value)} className="k-input" placeholder="e.g., Her younger brother" data-testid={`char-relationship-${i}`} /></Field>
              <Field label="Age range"><Input value={c.age_range} onChange={(e) => updateChar(i, "age_range", e.target.value)} className="k-input" placeholder="e.g., 30s" /></Field>
              <Field label="Personality traits"><Input value={c.personality} onChange={(e) => updateChar(i, "personality", e.target.value)} className="k-input" placeholder="e.g., calm, witty, generous" /></Field>
              <Field label="Physical description"><Input value={c.physical_description} onChange={(e) => updateChar(i, "physical_description", e.target.value)} className="k-input" placeholder="optional" /></Field>
              <Field label="Habits / quirks"><Input value={c.quirks} onChange={(e) => updateChar(i, "quirks", e.target.value)} className="k-input" placeholder="e.g., starts every call with 'tell me everything'" /></Field>
              <Field label="Things they often say"><Input value={c.catchphrases} onChange={(e) => updateChar(i, "catchphrases", e.target.value)} className="k-input" /></Field>
            </div>
            <Field label="Role in the story"><Textarea value={c.emotional_role} onChange={(e) => updateChar(i, "emotional_role", e.target.value)} className="k-input min-h-[70px]" placeholder="e.g., The person who held everything together when we lost dad." /></Field>
          </div>
        ))}
        <button onClick={addChar} className="btn-ghost" data-testid="add-character">
          <Plus size={16} className="mr-1.5" /> Add another character
        </button>
      </div>
    </div>
  );
}

function Step6({ d, setD, onUploadPhotos, meta, onUploadAIRefs, uploading }) {
  const fileRef = useRef(null);
  const aiRefRef = useRef(null);
  const updatePhoto = (idx, key, value) => {
    setD((prev) => {
      const list = [...prev.photos];
      list[idx] = { ...list[idx], [key]: value };
      return { ...prev, photos: list };
    });
  };
  const removePhoto = (idx) => setD((prev) => ({ ...prev, photos: prev.photos.filter((_, i) => i !== idx) }));
  const removeAIRef = (id) => setD((prev) => ({ ...prev, ai_photo_reference_ids: prev.ai_photo_reference_ids.filter((x) => x !== id) }));

  return (
    <div>
      <SectionTitle overline="Step 5 · Photos" title="Upload photos for the book" hint="You can add up to 10 photos. Mark any you want printed directly as a page in the book." />
      <div className="rounded-xl border-2 border-dashed border-lineSoft p-6 sm:p-8 text-center bg-cream">
        <ImagePlus className="mx-auto text-chocolate" size={28} />
        <p className="font-serif text-lg text-ink mt-3">Choose your photographs</p>
        <p className="text-xs text-inkMute mt-1">Only upload images you have permission to use. <Link to="/privacy" className="underline" data-testid="photo-privacy-link">Read the privacy policy and review notes.</Link></p>
        <input ref={fileRef} type="file" accept="image/*" multiple disabled={uploading} aria-label="Upload photos" className="hidden"
          onChange={async (e) => { const input = e.target; await onUploadPhotos(input.files); input.value = ""; }} data-testid="photo-input" />
        <button disabled={uploading || d.photos.length >= 10} onClick={() => fileRef.current && fileRef.current.click()} className="btn-primary mt-5" data-testid="photo-upload-btn">
          Choose photos ({d.photos.length}/10)
        </button>
        {uploading && <p role="status" className="text-sm text-inkMute mt-4" data-testid="photo-upload-status">Uploading your photographs. Please keep this page open.</p>}
      </div>
      {d.photos.length > 0 && (
        <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-4">
          {d.photos.map((p, i) => (
            <div key={p.photo_id} className="rounded-xl bg-cream border border-lineSoft p-4">
              <div className="relative">
                <img src={`${API}/photos/${p.photo_id}`} alt={p.caption || `Uploaded photograph ${i + 1}`} className="w-full aspect-[4/3] object-contain rounded-lg" />
                <button onClick={() => removePhoto(i)} aria-label={`Remove photograph ${i + 1} from book`} className="absolute top-2 right-2 h-11 w-11 inline-flex items-center justify-center rounded-full bg-cream/90 text-ink border border-lineSoft" data-testid={`remove-photo-${i}`}>
                  <X size={14} />
                </button>
              </div>
              <div className="mt-3 space-y-2">
                <Input value={p.caption} onChange={(e) => updatePhoto(i, "caption", e.target.value)} className="k-input" placeholder="Caption" aria-label={`Photo ${i + 1} caption`} data-testid={`photo-caption-${i}`} />
                <div className="grid grid-cols-2 gap-2">
                  <Input value={p.when_taken} onChange={(e) => updatePhoto(i, "when_taken", e.target.value)} className="k-input" placeholder="When" aria-label={`Photo ${i + 1} date`} data-testid={`photo-date-${i}`} />
                  <Input value={p.where_taken} onChange={(e) => updatePhoto(i, "where_taken", e.target.value)} className="k-input" placeholder="Where" aria-label={`Photo ${i + 1} location`} data-testid={`photo-location-${i}`} />
                </div>
                <Input value={p.people_in_photo} onChange={(e) => updatePhoto(i, "people_in_photo", e.target.value)} className="k-input" placeholder="Who's in this photo?" aria-label={`People in photo ${i + 1}`} data-testid={`photo-people-${i}`} />
                <Textarea value={p.why_important} onChange={(e) => updatePhoto(i, "why_important", e.target.value)} className="k-input min-h-[60px]" placeholder="Why is this photo important?" aria-label={`Why photo ${i + 1} matters`} data-testid={`photo-importance-${i}`} />
                <div className="space-y-2 pt-1">
                  <label className="flex items-center justify-between gap-3 text-sm text-ink p-2.5 rounded-lg bg-surface border border-lineSoft">
                    <span>
                      <span className="font-medium">Print this photo as a full page in the book</span>
                      <span className="block text-xs text-inkMute">Inserts the actual photograph at a memory-related chapter</span>
                    </span>
                    <Switch checked={p.use_in_book} onCheckedChange={(v) => updatePhoto(i, "use_in_book", v)} data-testid={`use-in-book-${i}`} />
                  </label>
                  <label className="flex items-center justify-between gap-3 text-sm text-ink p-2.5 rounded-lg bg-surface border border-lineSoft">
                    <span>
                      <span className="font-medium">Use as a character reference</span>
                      <span className="block text-xs text-inkMute">Keep this image with your project’s character notes</span>
                    </span>
                    <Switch checked={p.use_as_character_reference} onCheckedChange={(v) => updatePhoto(i, "use_as_character_reference", v)} data-testid={`use-as-ref-${i}`} />
                  </label>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* AI photo generation option */}
      <div className="mt-8 rounded-2xl bg-surface border border-lineSoft p-6 sm:p-8">
        <div className="flex items-start justify-between gap-4">
          <div className="flex-1">
            <p className="k-overline mb-2">Optional · Generate photos on your own</p>
            <p className="font-serif text-xl text-ink leading-tight">Don't have enough photos? We can illustrate the book for you.</p>
            <p className="text-sm text-inkMute mt-2">Pick a style and upload a reference image (optional) so we know what the characters look like.</p>
          </div>
          <Switch checked={d.ai_photos_enabled} onCheckedChange={(v) => setD((p) => ({ ...p, ai_photos_enabled: v }))} aria-label="Choose AI illustration preferences" data-testid="ai-photos-toggle" />
        </div>
        {d.ai_photos_enabled && (
          <div className="mt-6 space-y-5">
            <div>
              <Label className="k-label">Style of the illustrations</Label>
              <div className="flex flex-wrap gap-2">
                {(meta?.photo_styles || []).map((s) => (
                  <Chip key={s} active={d.ai_photo_style === s} onClick={() => setD((p) => ({ ...p, ai_photo_style: s }))} testId={`ai-style-${s}`}>{s}</Chip>
                ))}
              </div>
            </div>
            <div>
              <Label className="k-label">Reference photos (optional)</Label>
              <p className="text-xs text-inkMute mb-3">Stored with your project for reference. AI images currently use your written descriptions.</p>
              <input ref={aiRefRef} type="file" accept="image/*" multiple disabled={uploading} aria-label="Upload reference photos" className="hidden" onChange={async (e) => { const input = e.target; await onUploadAIRefs(input.files); input.value = ""; }} data-testid="ai-ref-input" />
              <button disabled={uploading || d.ai_photo_reference_ids.length >= 5} onClick={() => aiRefRef.current && aiRefRef.current.click()} className="btn-ghost" data-testid="ai-ref-upload-btn">
                <ImagePlus size={14} className="mr-1.5" /> Upload reference photos
              </button>
              {d.ai_photo_reference_ids.length > 0 && (
                <div className="mt-4 grid grid-cols-3 sm:grid-cols-5 gap-3">
                  {d.ai_photo_reference_ids.map((id) => (
                    <div key={id} className="relative">
                      <img src={`${API}/photos/${id}`} alt="ref" className="w-full aspect-square object-cover rounded-lg border border-lineSoft" />
                      <button onClick={() => removeAIRef(id)} aria-label="Remove reference photograph" className="absolute top-1 right-1 h-11 w-11 inline-flex items-center justify-center rounded-full bg-cream/95 text-ink border border-lineSoft" data-testid={`remove-ai-ref-${id}`}>
                        <X size={12} />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
            <div>
              <Label className="k-label">Anything specific about the illustrations?</Label>
              <Textarea value={d.ai_photo_notes} onChange={(e) => setD((p) => ({ ...p, ai_photo_notes: e.target.value }))} className="k-input min-h-[70px]" placeholder="e.g., warm lighting, both characters at sunset, soft colors" data-testid="ai-photo-notes" />
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function Step7({ d, setD }) {
  const set = (k, v) => setD((prev) => ({ ...prev, main_memory: { ...prev.main_memory, [k]: v } }));
  const m = d.main_memory;
  return (
    <div>
      <SectionTitle overline="Step 6 · Main memory" title="Tell us the memory the book should be built around." hint="Be as specific as possible. The little details are what makes it theirs." />
      <Field label="Short title of the memory"><Input value={m.title} onChange={(e) => set("title", e.target.value)} className="k-input" data-testid="main-memory-title" /></Field>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <Field label="When did it happen?"><Input value={m.date} onChange={(e) => set("date", e.target.value)} className="k-input" placeholder="e.g., Winter 2017" /></Field>
        <Field label="Where did it happen?"><Input value={m.location} onChange={(e) => set("location", e.target.value)} className="k-input" /></Field>
      </div>
      <Field label="Who was involved?"><Input value={m.people_involved} onChange={(e) => set("people_involved", e.target.value)} className="k-input" /></Field>
      <Field label="What happened?" required hint="A few rough notes are enough. What do you remember most?"><Textarea value={m.story_details} onChange={(e) => set("story_details", e.target.value)} className="k-input min-h-[120px]" data-testid="main-memory-story" /></Field>
      <Field label="Why does this memory matter?"><Textarea value={m.why_matters} onChange={(e) => set("why_matters", e.target.value)} className="k-input min-h-[80px]" /></Field>
      <Field label="What do you never want them to forget about this moment?"><Textarea value={m.never_forget} onChange={(e) => set("never_forget", e.target.value)} className="k-input min-h-[80px]" /></Field>
      <Field label="Any specific dialogue, inside joke, or phrase to include?"><Textarea value={m.dialogue} onChange={(e) => set("dialogue", e.target.value)} className="k-input min-h-[80px]" /></Field>
      <Field label="Any detail we must not get wrong?"><Textarea value={m.avoid_notes} onChange={(e) => set("avoid_notes", e.target.value)} className="k-input min-h-[60px]" /></Field>
    </div>
  );
}

function Step8({ d, setD }) {
  const update = (i, k, v) => setD((prev) => {
    const list = [...prev.memories]; list[i] = { ...list[i], [k]: v };
    return { ...prev, memories: list };
  });
  const add = () => setD((prev) => ({ ...prev, memories: [...prev.memories, makeEmptyMemory()] }));
  const remove = (i) => setD((prev) => ({ ...prev, memories: prev.memories.filter((_, idx) => idx !== i) }));

  return (
    <div>
      <SectionTitle overline="Step 7 · More memories" title="The other moments worth keeping." hint="Add a few more memories if you like. A sentence or two is enough, and you can leave this step blank." />
      <div className="space-y-5">
        {d.memories.map((m, i) => (
          <div key={i} className="rounded-xl bg-cream border border-lineSoft p-5">
            <div className="flex items-center justify-between mb-3">
              <p className="font-serif text-lg text-ink">Memory {i + 1}</p>
              {d.memories.length > 1 && (
                <button onClick={() => remove(i)} className="text-xs text-inkMute hover:text-chocolate inline-flex items-center gap-1" data-testid={`remove-mem-${i}`}>
                  <Trash2 size={14} /> Remove
                </button>
              )}
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Field label="Memory title"><Input value={m.title} onChange={(e) => update(i, "title", e.target.value)} className="k-input" data-testid={`mem-title-${i}`} /></Field>
              <Field label="People involved"><Input value={m.people_involved} onChange={(e) => update(i, "people_involved", e.target.value)} className="k-input" /></Field>
            </div>
            <Field label="What happened?"><Textarea value={m.story_details} onChange={(e) => update(i, "story_details", e.target.value)} className="k-input min-h-[80px]" /></Field>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Field label="Emotion attached to it"><Input value={m.emotion} onChange={(e) => update(i, "emotion", e.target.value)} className="k-input" placeholder="e.g., quiet relief" /></Field>
              <Field label="Quote / dialogue (optional)"><Input value={m.dialogue} onChange={(e) => update(i, "dialogue", e.target.value)} className="k-input" /></Field>
            </div>
            <Field label="Importance">
              <div className="flex gap-2">
                {[1,2,3,4,5].map((n) => (
                  <Chip key={n} active={m.importance_rating === n} onClick={() => update(i, "importance_rating", n)} testId={`memory-${i}-importance-${n}`}>{n}</Chip>
                ))}
              </div>
            </Field>
          </div>
        ))}
        <button onClick={add} className="btn-ghost" data-testid="add-memory">
          <Plus size={16} className="mr-1.5" /> Add another memory
        </button>
      </div>
    </div>
  );
}

function Step9({ d, set }) {
  return (
    <div>
      <SectionTitle overline="Step 8 · Personal message" title="Your dedication and final message" hint="A few honest sentences are enough. We'll shape the writing around them." />
      <Field label="Opening dedication"><Textarea value={d.opening_dedication} onChange={(e) => set("opening_dedication", e.target.value)} className="k-input min-h-[80px]" placeholder="For my mother, who packed my dreams before her own." data-testid="opening-dedication" /></Field>
      <Field label="Final message"><Textarea value={d.final_message} onChange={(e) => set("final_message", e.target.value)} className="k-input min-h-[80px]" data-testid="final-message-intent" /></Field>
      <Field label="Anything you always wanted to say but never did?"><Textarea value={d.never_said} onChange={(e) => set("never_said", e.target.value)} className="k-input min-h-[80px]" /></Field>
      <Field label="What should the recipient feel after reading the book?"><Textarea value={d.feel_after_reading} onChange={(e) => set("feel_after_reading", e.target.value)} className="k-input min-h-[60px]" /></Field>
      <Field label="Should the final page be…">
        <Select value={d.final_page_style} onValueChange={(v) => set("final_page_style", v)}>
          <SelectTrigger className="k-input" aria-label="Final page style" data-testid="final-page-style"><SelectValue placeholder="Pick a style" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="emotional">Emotional</SelectItem>
            <SelectItem value="funny">Funny</SelectItem>
            <SelectItem value="poetic">Poetic</SelectItem>
            <SelectItem value="simple">Simple</SelectItem>
          </SelectContent>
        </Select>
      </Field>
    </div>
  );
}

function Step10({ d, set, setD, meta }) {
  const toggleStyle = (s) => {
    setD((prev) => {
      const list = new Set(prev.writing_styles || []);
      if (list.has(s)) list.delete(s);
      else list.add(s);
      return { ...prev, writing_styles: Array.from(list) };
    });
  };
  return (
    <div>
      <SectionTitle overline="Step 9 · Style" title="Pick the voice of your book" />
      <Field label="Narration">
        <Select value={d.narration} onValueChange={(v) => set("narration", v)}>
          <SelectTrigger className="k-input" aria-label="Narration" data-testid="narration-select"><SelectValue /></SelectTrigger>
          <SelectContent>
            {meta.narration_options.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
          </SelectContent>
        </Select>
      </Field>
      <Field label="Language">
        <Select value={d.language} onValueChange={(v) => set("language", v)}>
          <SelectTrigger className="k-input" aria-label="Language" data-testid="language-select"><SelectValue /></SelectTrigger>
          <SelectContent>
            {meta.languages.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
          </SelectContent>
        </Select>
      </Field>
      <Field label="Writing style" hint="Pick one or several. We’ll blend them.">
        <div className="flex flex-wrap gap-2">
          {meta.writing_styles.map((s) => (
            <Chip key={s} active={(d.writing_styles || []).includes(s)} onClick={() => toggleStyle(s)} testId={`style-${s}`}>{s}</Chip>
          ))}
        </div>
      </Field>
    </div>
  );
}

function Step11({ d, setD, meta }) {
  const setLen = (v) => setD((p) => ({ ...p, book_length: v }));
  const updateChapter = (i, v) => setD((p) => { const list = [...p.chapters]; list[i] = v; return { ...p, chapters: list }; });
  const addChapter = () => setD((p) => ({ ...p, chapters: [...p.chapters, "New chapter"] }));
  const removeChapter = (i) => setD((p) => ({ ...p, chapters: p.chapters.filter((_, idx) => idx !== i) }));
  const moveChapter = (i, dir) => setD((p) => {
    const list = [...p.chapters];
    const j = i + dir;
    if (j < 0 || j >= list.length) return p;
    [list[i], list[j]] = [list[j], list[i]];
    return { ...p, chapters: list };
  });
  return (
    <div>
      <SectionTitle overline="Step 10 · Structure" title="Choose the length and chapters" />
      <Field label="Book length">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {meta.book_lengths.map((b) => (
            <button key={b.value} onClick={() => setLen(b.value)} aria-pressed={d.book_length === b.value} data-testid={`len-${b.value}`}
              className={`text-left rounded p-4 border transition-colors ${d.book_length === b.value ? 'bg-chocolate text-white border-chocolate' : 'bg-cream text-ink border-lineSoft'}`}>
              <p className="font-serif text-base sm:text-lg">{b.label.split('(')[0].trim()}</p>
              <p className={`text-xs mt-1 ${d.book_length === b.value ? 'text-cream/80' : 'text-inkMute'}`}>{b.label.split('(')[1]?.replace(')', '')}</p>
            </button>
          ))}
        </div>
      </Field>
      <p className="text-xs text-inkMute mb-6" data-testid="page-count-disclosure">These are writing-length targets, not a guaranteed printed page count. Confirm final print specifications with us.</p>
      <Label className="k-label mt-2">Chapters (rename, reorder, or remove)</Label>
      <div className="space-y-2">
        {d.chapters.map((c, i) => (
          <div key={i} className="flex items-center gap-1.5 sm:gap-2">
            <span className="text-xs text-inkMute w-5 sm:w-6 text-right shrink-0">{i + 1}.</span>
            <Input value={c} onChange={(e) => updateChapter(i, e.target.value)} aria-label={`Chapter ${i + 1} title`} className="k-input flex-1 min-w-0 !px-3 sm:!px-4 text-sm sm:text-base" data-testid={`chapter-${i}`} />
            <button onClick={() => moveChapter(i, -1)} disabled={i === 0} className="h-8 w-8 sm:h-9 sm:w-9 inline-flex items-center justify-center rounded-full border border-lineSoft text-ink hover:bg-surface disabled:opacity-40 disabled:cursor-not-allowed shrink-0" data-testid={`up-chapter-${i}`} aria-label="Move up">
              <ArrowUp size={14} />
            </button>
            <button onClick={() => moveChapter(i, 1)} disabled={i === d.chapters.length - 1} className="h-8 w-8 sm:h-9 sm:w-9 inline-flex items-center justify-center rounded-full border border-lineSoft text-ink hover:bg-surface disabled:opacity-40 disabled:cursor-not-allowed shrink-0" data-testid={`down-chapter-${i}`} aria-label="Move down">
              <ArrowDown size={14} />
            </button>
            {d.chapters.length > 1 && (
              <button onClick={() => removeChapter(i)} className="h-8 w-8 sm:h-9 sm:w-9 inline-flex items-center justify-center rounded-full border border-lineSoft text-inkMute hover:text-chocolate hover:border-chocolate shrink-0" data-testid={`remove-chapter-${i}`} aria-label="Remove chapter">
                <Trash2 size={14} />
              </button>
            )}
          </div>
        ))}
      </div>
      <button onClick={addChapter} className="btn-ghost mt-3" data-testid="add-chapter">
        <Plus size={16} className="mr-1.5" /> Add chapter
      </button>
    </div>
  );
}

function Step12({ d, set }) {
  return (
    <div>
      <SectionTitle overline="Step 11 · Sensitivities" title="Anything we should be careful of?" hint="Skip anything that doesn't apply." />
      <Field label="Topics to avoid"><Textarea value={d.avoid_topics} onChange={(e) => set("avoid_topics", e.target.value)} className="k-input min-h-[60px]" /></Field>
      <Field label="People not to mention"><Input value={d.avoid_people} onChange={(e) => set("avoid_people", e.target.value)} className="k-input" /></Field>
      <Field label="Sensitive memories to handle gently"><Textarea value={d.sensitive_memories} onChange={(e) => set("sensitive_memories", e.target.value)} className="k-input min-h-[60px]" /></Field>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-3">
        <div className="flex items-center justify-between p-4 rounded-xl bg-cream border border-lineSoft">
          <Label className="k-label !mb-0">Avoid being too emotional</Label>
          <Switch checked={d.avoid_emotional} onCheckedChange={(v) => set("avoid_emotional", v)} aria-label="Avoid being too emotional" data-testid="avoid-emotional" />
        </div>
        <div className="flex items-center justify-between p-4 rounded-xl bg-cream border border-lineSoft">
          <Label className="k-label !mb-0">Avoid romantic wording</Label>
          <Switch checked={d.avoid_romantic} onCheckedChange={(v) => set("avoid_romantic", v)} aria-label="Avoid romantic wording" data-testid="avoid-romantic" />
        </div>
      </div>
      <Field label="Avoid jokes about…"><Input value={d.avoid_jokes} onChange={(e) => set("avoid_jokes", e.target.value)} className="k-input" /></Field>
    </div>
  );
}

function Step13({ d }) {
  return (
    <div>
      <SectionTitle overline="Step 12 · Your keepsake" title="A story only you could tell." hint="Take a moment to check your answers. Next, choose your book and extras before checkout. Nothing is charged here." />
      <div className="rounded-xl bg-cream border border-lineSoft p-6">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm text-ink">
          <p data-testid="summary-recipient"><span className="text-inkMute">For:</span> {d.recipient_name || "Not provided"} ({d.relationship_type || "Not provided"})</p>
          <p data-testid="summary-from"><span className="text-inkMute">From:</span> {d.your_name || "Not provided"}</p>
          <p data-testid="summary-occasion"><span className="text-inkMute">Occasion:</span> {d.occasion || "Not provided"}</p>
          <p><span className="text-inkMute">Product:</span> Keepsake Storybook</p>
          <p data-testid="summary-tones"><span className="text-inkMute">Tones:</span> {(d.tones || []).join(' · ') || "Not selected"}</p>
          <p><span className="text-inkMute">Memories:</span> {(d.memories?.length || 0) + (d.main_memory?.title ? 1 : 0)}</p>
          <p><span className="text-inkMute">Photos:</span> {d.photos?.length || 0}</p>
          <p><span className="text-inkMute">Chapters:</span> {d.chapters?.length || 0}</p>
          {d.ai_photos_enabled && (
            <p className="sm:col-span-2"><span className="text-inkMute">AI illustrations:</span> {d.ai_photo_style || "no style chosen yet"}</p>
          )}
        </div>
      </div>
      <p className="text-xs text-inkMute mt-5">
        After ordering, AI-assisted writing turns your memories into a personal draft. Find it in Your books when it’s ready. Contact us before ordering to confirm revisions, print approval and delivery arrangements.
      </p>
    </div>
  );
}
